import { useEffect, useState } from 'react';
import { useCurrentActivity } from '../lib/queries';

/**
 * **La pastille sur « Activité du jour » : ce qui dit à un élève qu'une consigne est arrivée.**
 *
 * Répétition générale du 7 septembre 2026 : la promesse de l'écran enseignant (« Vos élèves verront
 * la consigne apparaître sur leur bureau sans avoir à recharger leur page ») n'était tenue **qu'à
 * moitié**. La consigne était bien fraîche quand l'élève ouvrait « Activité du jour » — mais rien
 * n'apparaissait sur son bureau : aucune pastille, aucune notification. Un élève assis devant le
 * Tableur en plein écran ne savait pas qu'une consigne venait d'être lancée.
 *
 * Ce hook rend la moitié manquante. Il ne fabrique aucune notion nouvelle : la consigne en cours
 * vient de `activities.current`, que le fil temps réel rafraîchit en quelques secondes
 * (`lib/useRealtimeSignals.ts`, signal `core.activity.changed`).
 *
 * ## « Vue » veut dire vue, et ça se retient
 *
 * L'identifiant de la dernière consigne réellement ouverte est gardé dans `localStorage`, par
 * utilisateur — pas dans `sessionStorage` : une pastille qui reviendrait à chaque rechargement de
 * page finirait par ne plus rien vouloir dire, exactement comme une notification qu'on ne peut pas
 * faire taire. `localStorage` est le bon endroit et le seul : c'est un confort d'affichage propre à
 * ce navigateur-là, jamais un état que le cœur aurait à connaître.
 *
 * Stockage refusé (navigation privée stricte) : la pastille reste correcte pour la session en
 * cours et reparaît après un rechargement. Dégradé, jamais cassé.
 *
 * ## Pourquoi l'ajustement se fait pendant le rendu
 *
 * Marquer « vue » est un ajustement d'état à partir d'une entrée qui change — le patron que React
 * prescrit, et celui que `desk/Desk.tsx` suit déjà pour l'adresse. Un `setState` posé dans un effet
 * ferait un rendu de plus avec la pastille encore allumée alors que l'élève regarde déjà la
 * consigne (et `react-hooks/set-state-in-effect` le refuse). L'effet ci-dessous ne fait donc
 * qu'écrire dans le stockage — jamais changer d'état.
 */

const SEEN_KEY_PREFIX = 'ndrc.activitySeen.';

function readSeen(userId: string): string | null {
  try {
    return localStorage.getItem(SEEN_KEY_PREFIX + userId);
  } catch {
    return null;
  }
}

function writeSeen(userId: string, activityId: string): void {
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + userId, activityId);
  } catch {
    // Voir le commentaire de tête : sans stockage, la pastille reparaît au rechargement.
  }
}

export interface ActivityBadgeInput {
  userId: string;
  /** La classe dont on lit la consigne — `undefined` laisse `activities.current` la résoudre seul
   * (un élève n'en a qu'une). */
  classId: string | undefined;
  /** `true` quand la fenêtre « Activité du jour » est ouverte : l'élève A la consigne sous les
   * yeux, la pastille n'a plus rien à annoncer. */
  opened: boolean;
}

/** `true` quand une consigne existe et que cet élève ne l'a pas encore ouverte. */
export function useActivityBadge({ userId, classId, opened }: ActivityBadgeInput): boolean {
  const current = useCurrentActivity(classId);
  const activityId = current.data?.activity?.id ?? null;

  const [seen, setSeen] = useState(() => readSeen(userId));

  // L'unique passage « la fenêtre est ouverte → cette consigne est vue », pendant le rendu. Ne peut
  // pas boucler : `seen` vaut `activityId` dès le passage suivant.
  if (opened && activityId !== null && activityId !== seen) {
    setSeen(activityId);
  }

  useEffect(() => {
    if (seen !== null) writeSeen(userId, seen);
  }, [seen, userId]);

  return activityId !== null && activityId !== seen;
}
